"use client";

// TypeScript declarations for Klaviyo SDK
declare global {
  interface Window {
    klaviyo?: {
      identify: (properties: Record<string, any>) => void;
      track: (eventName: string, properties?: Record<string, any>) => void;
      isIdentified: () => boolean;
      push: (args: any[]) => void;
    };
    _learnq?: any[];
  }
}

export interface KlaviyoUserProperties {
  $email?: string;
  $first_name?: string;
  $last_name?: string;
  $phone_number?: string;
  [key: string]: any;
}

/**
 * Check if Klaviyo SDK is loaded
 */
export function isKlaviyoLoaded(): boolean {
  return typeof window !== "undefined" && !!window.klaviyo;
}

/**
 * Wait for Klaviyo SDK to be loaded
 */
export function waitForKlaviyo(timeout = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isKlaviyoLoaded()) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (isKlaviyoLoaded()) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        reject(new Error("Klaviyo SDK failed to load"));
      }
    }, 100);
  });
}

/**
 * Identify a user in Klaviyo
 */
export async function identifyUser(
  properties: KlaviyoUserProperties
): Promise<void> {
  try {
    await waitForKlaviyo();

    if (!properties.$email) {
      console.warn("Klaviyo: Cannot identify user without email");
      return;
    }

    window.klaviyo?.identify(properties);
    console.log("Klaviyo: User identified", properties.$email);
  } catch (error) {
    console.error("Klaviyo: Failed to identify user", error);
  }
}

/**
 * Track a custom event in Klaviyo
 */
export async function trackEvent(
  eventName: string,
  properties?: Record<string, any>
): Promise<void> {
  try {
    await waitForKlaviyo();
    window.klaviyo?.track(eventName, properties);
    console.log("Klaviyo: Event tracked", eventName, properties);
  } catch (error) {
    console.error("Klaviyo: Failed to track event", error);
  }
}

/**
 * Track a page view in Klaviyo
 */
export async function trackPageView(
  pagePath?: string,
  pageTitle?: string
): Promise<void> {
  const properties: Record<string, any> = {};

  if (pagePath) properties.page = pagePath;
  if (pageTitle) properties.title = pageTitle;

  await trackEvent("Navigate on Site", properties);
}

/**
 * Check if a user is currently identified
 */
export function isUserIdentified(): boolean {
  if (!isKlaviyoLoaded()) return false;
  return window.klaviyo?.isIdentified() ?? false;
}

/**
 * Reset/logout the current user
 */
export async function resetUser(): Promise<void> {
  try {
    await waitForKlaviyo();
    // Klaviyo doesn't have a built-in reset, so we clear by identifying with empty email
    // This effectively creates a new anonymous profile
    if (window._learnq) {
      window._learnq.push(["clearIdentity"]);
    }
    console.log("Klaviyo: User reset");
  } catch (error) {
    console.error("Klaviyo: Failed to reset user", error);
  }
}
